import {
  useQuery,
  QueryKey,
  QueryObserverOptions,
  UseQueryResult,
  MutationKey,
  useMutation,
  MutateOptions,
} from "react-query";

import {
  organizationListShortService,
  organizationListService,
  organizationDetailService,
  organizationUpdateService,
  organizationCreateService,
  organizationUpdateStatusService,
} from "./services";
import { OrganizationListShortResponse } from "./types";

type OrganizationListResponse = Awaited<
  ReturnType<typeof organizationListService>
>;

type OrganizationDetailResponse = Awaited<
  ReturnType<typeof organizationDetailService>
>;

export const useOrganizations = ({
  queryKey,
  params,
  options,
}: {
  queryKey?: QueryKey | string;
  params?: Parameters<typeof organizationListService>[0];
  options?: QueryObserverOptions<unknown, unknown, OrganizationListResponse>;
}) => {
  const response = useQuery({
    queryKey: queryKey ?? "organizationListHooks",
    queryFn: () => organizationListService(params),
    ...options,
  });

  return response as UseQueryResult<OrganizationListResponse>;
};

export const useShortOrganizations = ({
  queryKey,
  params,
  options,
}: {
  queryKey?: QueryKey | string;
  params?: Parameters<typeof organizationListShortService>[0];
  options?: QueryObserverOptions<
    unknown,
    unknown,
    OrganizationListShortResponse
  >;
}) => {
  const response = useQuery({
    queryKey: queryKey ?? "organizationListShortHooks",
    queryFn: () => organizationListShortService(params),
    ...options,
  });

  return response as UseQueryResult<OrganizationListShortResponse>;
};

export const useOrganizationDetail = ({
  queryKey,
  params,
  options,
}: {
  queryKey?: QueryKey | string;
  params?: Parameters<typeof organizationDetailService>[0];
  options?: QueryObserverOptions<unknown, unknown, OrganizationDetailResponse>;
}) => {
  const response = useQuery({
    queryKey: queryKey ?? "organizationDetailHooks",
    queryFn: () => organizationDetailService(params),
    ...options,
  });

  return response as UseQueryResult<OrganizationDetailResponse>;
};

export const useOrganizationUpdate = ({
  queryKey,
  options,
}: {
  queryKey?: MutationKey | string;
  options?: MutateOptions<
    OrganizationDetailResponse,
    unknown,
    Parameters<typeof organizationUpdateService>[0]
  >;
}) => {
  const response = useMutation({
    mutationKey: queryKey ?? "organizationUpdateService",
    mutationFn: organizationUpdateService,
    onSuccess: options?.onSuccess as unknown as () => void,
    onError: options?.onError as unknown as () => void,
  });

  return response;
};

export const useOrganizationCreate = ({
  queryKey,
  options,
}: {
  queryKey?: MutationKey | string;
  options?: MutateOptions<
    OrganizationDetailResponse,
    unknown,
    Parameters<typeof organizationCreateService>[0]
  >;
}) => {
  const response = useMutation({
    mutationKey: queryKey ?? "organizationCreateService",
    mutationFn: organizationCreateService,
    onSuccess: options?.onSuccess as unknown as () => void,
    onError: options?.onError as unknown as () => void,
  });

  return response;
};

export const useOrganizationUpdateStatus = ({
  queryKey,
  options,
}: {
  queryKey?: MutationKey | string;
  options?: MutateOptions<
    OrganizationDetailResponse,
    unknown,
    Parameters<typeof organizationUpdateStatusService>[0]
  >;
}) => {
  const response = useMutation({
    mutationKey: queryKey ?? "organizationUpdateStatusService",
    mutationFn: organizationUpdateStatusService,
    onSuccess: options?.onSuccess as unknown as () => void,
    onError: options?.onError as unknown as () => void,
  });

  return response;
};
